import React, { useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { SearchBar } from '../../common/SearchBar'
import { FindSocietyListItem } from './FindSocietyListItem'
import { FormControl } from '../../common/FormControl'
import { theme } from '../../../theme/theme'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { FindSocietyContent } from './FindSocietyContent'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { societyQueries } from '../../../api/societies'
import { ISocietiesModel } from '../../../interfaces/models/societies.interfaces'
import { useAuth } from '../../../hooks/useAuth'
import { i18nSocietyType } from '../../../i18n'

export const FindSociety = (): React.ReactNode => {
  const { translate } = useAppTranslation()

  const { userId } = useAuth()

  const [selectedSociety, setSelectedSociety] = useState<
    ISocietiesModel | undefined
  >()
  const [searchString, setSearchString] = useState('')
  const isMobile = useIsMobile()
  const debounceString = useDebounce(searchString, 400)
  const { data: matchingSocieties, isLoading: isFindingSociety } = useQuery(
    societyQueries.searchSocieties(debounceString.trim())
  )

  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const userSocietyIds = userSocieties?.map((_society) => _society._id) || []
  const loading = isFindingSociety

  const onFocusSearch = (): void => {
    if (isMobile) {
      setSelectedSociety(undefined)
    }
  }

  const [header1, header2] = translate('findSocietyView.header', {
    context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
  }).split('Boappa')

  return (
    <div
      className={`flex overflow-y-auto h-full ${isMobile ? 'flex-col' : ''}`}
    >
      <div
        className={`flex 
 flex-1 flex-col px-0 pt-4 md:flex-1 md:px-14 md:pt-0 ${
   !isMobile ? 'border-r' : ''
 }`}
      >
        <h2 style={theme.textVariants.h1}>
          {header1}
          {header2}{' '}
        </h2>

        <p style={theme.textVariants.paragraph} className="mt-4">
          {translate('findSocietyView.description', {
            context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
          })}
        </p>
        <p style={theme.textVariants.paragraph} className="mt-4">
          {translate('findSocietyView.description2', {
            context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
          })}
        </p>
        <p style={theme.textVariants.paragraph} className="mt-4">
          {translate('findSocietyView.description3', {
            context: i18nSocietyType(selectedSociety?.societyTypeDisplayName),
          })}
        </p>

        <FormControl
          className="mt-4"
          name="searchbar"
          label={translate('findSociety.labels.search')}
        >
          <SearchBar
            widthClassName="w-full"
            className="w-full pr-6"
            value={searchString}
            onChange={(e) => setSearchString(e.currentTarget.value)}
            onFocus={onFocusSearch}
          />
        </FormControl>
        {loading ? (
          <SpinnerWrapper className="mt-4" />
        ) : (
          <>
            {matchingSocieties && matchingSocieties.length > 0 && (
              <div className="mt-6 flex min-h-0 grow flex-col overflow-auto pr-6">
                {matchingSocieties.map((_society) =>
                  !userSocietyIds.includes(_society._id) ? (
                    <FindSocietyListItem
                      selected={selectedSociety?._id === _society._id}
                      onClick={() => {
                        setSelectedSociety(_society)
                        if (isMobile) {
                          setSearchString('')
                        }
                      }}
                      key={_society._id}
                      society={_society}
                    />
                  ) : (
                    <FindSocietyListItem
                      selected={selectedSociety?._id === _society._id}
                      key={_society._id}
                      society={_society}
                    />
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="ml-0 mt-6 flex-2 overflow-y-auto py-4 px-2 md:mt-0 md:ml-6 md:flex-1 md:py-0 md:px-14">
        {selectedSociety && <FindSocietyContent society={selectedSociety} />}
      </div>
    </div>
  )
}
